import { endOfWeek, startOfWeek } from 'date-fns';
import moment from 'moment';

import { fetchOrders } from 'api/order';
import { Status } from 'types/items';
import { Order } from 'types/order';
import { Site } from 'types/site';
import { getSkuNameFromDetails } from 'utils/getSkuNameFromDetails';

const PAGE_SIZE = 500;
const MAX_PAGES = 40;

export type NameMap = Record<string, string>;

export type OrdersByCustomerRow = {
  key: string;
  dayKey: string;
  deliveryDate: string;
  customer: string;
  packages: string;
  count: number;
};

export type CasesPerDayRow = {
  key: string;
  dayKey: string;
  deliveryDate: string;
  packages: string;
  count: number;
};

export type TotalCasesRow = {
  key: string;
  packages: string;
  count: number;
  ready: number;
};

type OrderLine = {
  dayKey: string;
  deliveryDate: string;
  customer: string;
  skuId: string;
  packages: string;
  count: number;
};

/** Sunday–Saturday of the week containing `today`, in local time. */
export const currentWeekRange = (today = new Date()) => ({
  startDate: startOfWeek(today, { weekStartsOn: 0 }),
  endDate: endOfWeek(today, { weekStartsOn: 0 }),
});

/** Calendar day of `deliveryAt`, matching the UTC date stored on the order. */
export const toDeliveryDayKey = (deliveryAt?: string): string => {
  if (!deliveryAt) {
    return '';
  }

  return moment.utc(deliveryAt).format('YYYY-MM-DD');
};

/** Month/day/year only — same format the Orders table uses for Delivery Date. */
export const formatDeliveryDay = (deliveryAt?: string): string => {
  if (!deliveryAt) {
    return ' - ';
  }

  return moment.utc(deliveryAt).format('MM/DD/yyyy');
};

export const getCustomerName = (
  order: Order,
  siteNames: NameMap = {},
): string => {
  const site = (order.site as unknown) as Site | string | undefined;

  if (site && typeof site === 'object' && site.name) {
    return site.name;
  }

  const siteId = typeof site === 'string' ? site : site?._id || '';

  if (siteId && siteNames[siteId]) {
    return siteNames[siteId];
  }

  return ' - ';
};

export const getPackageName = (
  order: Order,
  skuId: string,
  skuNames: NameMap = {},
): string => {
  const fromDetails = getSkuNameFromDetails(order.skuDetails, skuId);

  if (fromDetails && fromDetails !== ' - ') {
    return fromDetails;
  }

  return skuNames[skuId] || ' - ';
};

const flattenOrderLines = (
  orders: Order[],
  skuNames: NameMap = {},
  siteNames: NameMap = {},
): OrderLine[] => {
  const lines: OrderLine[] = [];

  orders.forEach((order) => {
    const dayKey = toDeliveryDayKey(order.deliveryAt);
    const deliveryDate = formatDeliveryDay(order.deliveryAt);
    const customer = getCustomerName(order, siteNames);

    (order.details || []).forEach((detail) => {
      const skuId = detail?.sku || '';

      lines.push({
        dayKey,
        deliveryDate,
        customer,
        skuId,
        packages: getPackageName(order, skuId, skuNames),
        count: Number(detail?.casesCount) || 0,
      });
    });
  });

  return lines;
};

const compareName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

/** Rowspan for each index: group length at the start, 0 on continuation rows. */
export const groupSpans = <T>(
  rows: T[],
  keyFn: (row: T) => string,
): number[] => {
  const spans = Array(rows.length).fill(0);
  let index = 0;

  while (index < rows.length) {
    let span = 1;
    const key = keyFn(rows[index]);

    while (index + span < rows.length && keyFn(rows[index + span]) === key) {
      span += 1;
    }

    spans[index] = span;
    index += span;
  }

  return spans;
};

/**
 * Leaves Delivery Date / Customer blank when the previous row is in the same
 * group, matching the packed look of a merged spreadsheet cell.
 */
export const blankRepeatedGroupFields = <
  T extends { dayKey?: string; customer?: string; deliveryDate?: string }
>(
  rows: T[],
  fields: Array<'deliveryDate' | 'customer'>,
): T[] =>
  rows.map((row, index) => {
    const prev = rows[index - 1];
    const next = { ...row };

    if (fields.includes('deliveryDate') && prev && prev.dayKey === row.dayKey) {
      next.deliveryDate = '';
    }

    if (
      fields.includes('customer') &&
      prev &&
      prev.dayKey === row.dayKey &&
      prev.customer === row.customer
    ) {
      next.customer = '';
    }

    return next;
  });

/**
 * One row per delivery day + customer + SKU, summing `casesCount` when the
 * same customer orders the same package more than once on that day.
 */
export const buildOrdersByCustomer = (
  orders: Order[],
  skuNames: NameMap = {},
  siteNames: NameMap = {},
): OrdersByCustomerRow[] => {
  const grouped = new Map<string, OrdersByCustomerRow>();

  flattenOrderLines(orders, skuNames, siteNames).forEach((line) => {
    const key = `${line.dayKey}|${line.customer}|${line.skuId}`;
    const existing = grouped.get(key);

    if (existing) {
      existing.count += line.count;
      return;
    }

    grouped.set(key, {
      key,
      dayKey: line.dayKey,
      deliveryDate: line.deliveryDate,
      customer: line.customer,
      packages: line.packages,
      count: line.count,
    });
  });

  return Array.from(grouped.values()).sort((a, b) => {
    const byDay = a.dayKey.localeCompare(b.dayKey);
    if (byDay !== 0) {
      return byDay;
    }

    const byCustomer = compareName(a.customer, b.customer);
    if (byCustomer !== 0) {
      return byCustomer;
    }

    return compareName(a.packages, b.packages);
  });
};

/**
 * One row per delivery day + SKU, summing cases across every customer.
 */
export const buildCasesPerDay = (
  orders: Order[],
  skuNames: NameMap = {},
  siteNames: NameMap = {},
): CasesPerDayRow[] => {
  const grouped = new Map<string, CasesPerDayRow>();

  flattenOrderLines(orders, skuNames, siteNames).forEach((line) => {
    const key = `${line.dayKey}|${line.skuId}`;
    const existing = grouped.get(key);

    if (existing) {
      existing.count += line.count;
      return;
    }

    grouped.set(key, {
      key,
      dayKey: line.dayKey,
      deliveryDate: line.deliveryDate,
      packages: line.packages,
      count: line.count,
    });
  });

  return Array.from(grouped.values()).sort((a, b) => {
    const byDay = a.dayKey.localeCompare(b.dayKey);
    if (byDay !== 0) {
      return byDay;
    }

    return compareName(a.packages, b.packages);
  });
};

/**
 * One row per SKU, summing cases across every customer and day in range.
 */
export const buildTotalCases = (
  orders: Order[],
  skuNames: NameMap = {},
  siteNames: NameMap = {},
  readyBySkuId: Record<string, number> = {},
): TotalCasesRow[] => {
  const grouped = new Map<string, TotalCasesRow>();

  flattenOrderLines(orders, skuNames, siteNames).forEach((line) => {
    const key = line.skuId;
    const existing = grouped.get(key);

    if (existing) {
      existing.count += line.count;
      return;
    }

    grouped.set(key, {
      key,
      packages: line.packages,
      count: line.count,
      ready: 0,
    });
  });

  return Array.from(grouped.values())
    .map((row) => ({
      ...row,
      ready: readyBySkuId[row.key] ?? 0,
    }))
    .sort((a, b) => compareName(a.packages, b.packages));
};

/** When checked, the summary only totals orders still in Open. */
export const applyOpenOrdersFilter = (
  orders: Order[],
  openOnly: boolean,
): Order[] =>
  openOnly ? orders.filter((order) => order.status === Status.OPEN) : orders;

/** Walks paginated `/order/list` so the summary sees every order in range. */
export const fetchOrdersForSummary = async ({
  washHub,
  startDate,
  endDate,
}: {
  washHub: string;
  startDate: Date;
  endDate: Date;
}): Promise<Order[]> => {
  const orders: Order[] = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages && page <= MAX_PAGES) {
    const response = await fetchOrders({
      washHub,
      startDate,
      endDate,
      page,
      limit: PAGE_SIZE,
      excludeStatus: [Status.CUSTOMER_REQUEST, Status.REJECTED],
    });

    orders.push(...(response?.items || []));
    totalPages = response?.totalPages || 1;
    page += 1;
  }

  return orders;
};
